import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError, qs } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Champ, Kpi, Modale, Squelette, Vide } from '../components/ui';
import { centimes, dateFr, heureFr, isoJour, montant, nomClient } from '../lib/format';
import type { BilanMois, Contrat, EntreesDuMois, Mouvement, Rendement, Tiroir, Tresorerie } from '../api/types';

/** Le mois precedent / suivant, en 'YYYY-MM'. */
function moisDecale(mois: string, n: number): string {
  const [a, m] = mois.split('-').map(Number);
  const d = new Date(a!, (m ?? 1) - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
const libelleMois = (mois: string) => {
  const [a, m] = mois.split('-').map(Number);
  return new Date(a!, (m ?? 1) - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

const LIBELLE_JOUR: Record<string, string> = {
  loue: 'louée', reserve: 'réservée', entretien: 'au garage', libre: 'libre',
};
/** Le jour du mois affiché est-il aujourd'hui ? Sert au liseré sur le calendrier. */
function estAujourdhui(mois: string, index: number): boolean {
  const n = new Date();
  return mois === isoJour(n).slice(0, 7) && n.getDate() === index + 1;
}

interface Tiroirs { tiroirs: Tiroir[]; totalReferenceMinor: number; tresorerie: Tresorerie; deviseReference: string }
interface Devises { devises: { code: string; libelle: string; decimales: number }[]; taux: { devise: string; tauxAppliqueMinor: number; dateEffet: string | null; manquant?: boolean }[]; deviseReference: string }

/** Un tiroir par devise. Pas de melange, pas de conversion implicite. */
export default function Caisse() {
  const { peut, estAgent } = useAuth();
  const [t, setT] = useState<Tiroirs | null>(null);
  const [dev, setDev] = useState<Devises | null>(null);
  const [jour, setJour] = useState(isoJour(new Date()));
  /*
   * « Hichem aime beaucoup voir les finances par mois, du 1er au 31. »
   * La caisse ne se lisait qu'au jour le jour : impossible de repondre a
   * « combien Sabrina a-t-elle depense ce mois-ci ».
   */
  /*
   * Le mois d'abord.
   *
   * Le patron ouvre la caisse pour voir le mois et le rendement de ses
   * voitures, pas le detail de la journee. Le jour reste a un clic — c'est la
   * vue de Sabrina au comptoir, mais ce n'est pas la premiere question posee.
   */
  const [vue, setVue] = useState<'jour' | 'mois'>('mois');
  const [mois, setMois] = useState(() => isoJour(new Date()).slice(0, 7));
  const [bilan, setBilan] = useState<BilanMois | null>(null);
  const [rendement, setRendement] = useState<Rendement | null>(null);
  const [entrees, setEntrees] = useState<EntreesDuMois | null>(null);
  const [mvts, setMvts] = useState<Mouvement[]>([]);
  const [ecran, setEcran] = useState<'' | 'encaisser' | 'change' | 'cloture' | 'taux'>('');
  const [msg, setMsg] = useState('');
  // Un encaissement anonyme ne solde aucun dossier. Quand on arrive depuis un
  // contrat (?contrat=<id>), la modale s'ouvre pre-remplie et le mouvement est
  // rattache : c'est ce rattachement qui fait vivre le solde et la tresorerie.
  const [params, setParams] = useSearchParams();
  const [cible, setCible] = useState<Contrat | null>(null);
  const idContrat = params.get('contrat');

  useEffect(() => {
    if (!idContrat) { setCible(null); return; }
    api.get<{ contrat: Contrat }>(`/contrats/${idContrat}`)
      .then((d) => { setCible(d.contrat); setEcran('encaisser'); })
      .catch(() => setMsg("Ce dossier est introuvable — l'encaissement ne sera rattache a rien."));
  }, [idContrat]);

  const fermerEncaissement = () => {
    setEcran('');
    if (idContrat) { params.delete('contrat'); setParams(params, { replace: true }); }
  };

  const recharger = useCallback(() => {
    api.get<Tiroirs>('/caisse/tiroirs').then(setT);
    api.get<Mouvement[]>(`/caisse/mouvements${qs({ date: jour })}`).then(setMvts);
    api.get<Devises>('/caisse/devises').then(setDev);
  }, [jour]);
  useEffect(recharger, [recharger]);

  useEffect(() => {
    if (vue !== 'mois') return;
    setBilan(null);
    api.get<BilanMois>(`/caisse/bilan-mensuel${qs({ mois })}`).then(setBilan).catch(() => {});
    setRendement(null); setEntrees(null);
    api.get<Rendement>(`/caisse/rendement${qs({ mois })}`).then(setRendement).catch(() => {});
    api.get<EntreesDuMois>(`/caisse/entrees-du-mois${qs({ mois })}`).then(setEntrees).catch(() => {});
  }, [vue, mois]);

  if (!t || !dev) return <Squelette lignes={6} />;

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>Caisse</h1>
          <p>Un tiroir par devise · total consolidé en {t.deviseReference}</p>
        </div>
        <div className="actions">
          <button className={`btn btn-s ${vue === 'jour' ? 'btn-p' : ''}`} onClick={() => setVue('jour')}>Jour</button>
          <button className={`btn btn-s ${vue === 'mois' ? 'btn-p' : ''}`} onClick={() => setVue('mois')}>Mois</button>
          <button className="btn btn-p" onClick={() => setEcran('encaisser')}>Encaisser</button>
          {peut('modifier.finance') && <button className="btn" onClick={() => setEcran('change')}>Opération de change</button>}
          {peut('gerer.taux') && <button className="btn" onClick={() => setEcran('taux')}>Taux du jour</button>}
          <button className="btn" disabled={!peut('cloturer.caisse')} onClick={() => setEcran('cloture')}
                  title={peut('cloturer.caisse') ? undefined : "Réservé aux gérants"}>
            Clôturer la caisse
          </button>
        </div>
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}

      {vue === 'mois' && (
        <div style={{ marginBottom: 18 }}>
          <div className="actions" style={{ marginBottom: 12 }}>
            <button className="btn btn-s" onClick={() => setMois(moisDecale(mois, -1))}>‹</button>
            <b style={{ minWidth: 150, textAlign: 'center' }}>{libelleMois(mois)}</b>
            <button className="btn btn-s" onClick={() => setMois(moisDecale(mois, 1))}
                    disabled={mois >= isoJour(new Date()).slice(0, 7)}>›</button>
          </div>

          {bilan === null ? <Squelette lignes={4} /> : (
            <>
              <div className="grille g4" style={{ marginBottom: 14 }}>
                <div className="carte carte-p">
                  <div className="micro">Fonds de caisse posé</div>
                  <div className="v montant">{montant(bilan.fondsMinor)}</div>
                  <div className="mini sourdine">ce que vous avez mis dans la caisse ce mois-ci</div>
                </div>
                <div className="carte carte-p">
                  <div className="micro">Encaissé</div>
                  <div className="v montant" style={{ color: 'var(--succes)' }}>{montant(bilan.encaissementsMinor)}</div>
                </div>
                <div className="carte carte-p">
                  <div className="micro">Dépensé</div>
                  <div className="v montant" style={{ color: 'var(--danger)' }}>{montant(bilan.depensesMinor)}</div>
                  {bilan.fondsMinor > 0 && (
                    <div className="mini sourdine">
                      {Math.round((bilan.depensesMinor / bilan.fondsMinor) * 100)} % du fonds consommé
                    </div>
                  )}
                </div>
                <div className="carte carte-p signature-kpi">
                  <div className="micro">Solde de caisse</div>
                  <div className="v">{montant(bilan.soldeFinMinor)}</div>
                  <div className="decomposition">
                    <span>au 1er <b>{montant(bilan.soldeDebutMinor)}</b></span>
                    {bilan.transfertsSortantsMinor > 0 && <span>− versé en banque <b>{montant(bilan.transfertsSortantsMinor)}</b></span>}
                  </div>
                </div>
              </div>

              {/* La vraie question du patron : qui a depensé quoi. */}
              <Carte pad={false} titre="Qui a fait quoi ce mois-ci">
                <div className="enveloppe-tbl">
                  <table className="tbl">
                    <thead><tr><th>Personne</th><th className="droite">Opérations</th><th className="droite">Encaissé</th><th className="droite">Dépensé</th></tr></thead>
                    <tbody>
                      {bilan.parPersonne.map((p) => (
                        <tr key={p.nom}>
                          <td><b>{p.nom}</b></td>
                          <td className="droite num">{p.nbOperations}</td>
                          <td className="droite montant">{p.encaissementsMinor > 0 ? montant(p.encaissementsMinor) : '—'}</td>
                          <td className="droite montant" style={p.depensesMinor > 0 ? { color: 'var(--danger)' } : undefined}>
                            {p.depensesMinor > 0 ? montant(p.depensesMinor) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Carte>

              <p className="mini sourdine" style={{ marginTop: 12 }}>
                Chaque ligne est nominative et vient du journal : personne ne peut dépenser sans que
                son nom reste. Le fonds est la somme des apports du mois.
              </p>

              {/*
                * Le croquis du patron : une fiche par voiture, un petit calendrier
                * du mois, et ce qu'elle rapporte. Sa question n'est pas « quel est
                * le chiffre d'affaires » mais « laquelle de mes treize voitures est
                * restée sur le parking ce mois-ci ».
                */}
              {rendement && (
                <div style={{ marginTop: 20 }}>
                  <Carte titre={`Ce que chaque voiture a rapporté — ${libelleMois(mois)}`} action={
                    <div className="legende" style={{ margin: 0 }}>
                      <span><i style={{ background: 'var(--succes)' }} />Louée</span>
                      <span><i style={{ background: 'var(--violet)' }} />Réservée</span>
                      <span><i style={{ background: '#8A929E' }} />Entretien</span>
                      <span><i style={{ background: 'var(--danger-doux)', border: '1px solid var(--danger-bord)' }} />Libre</span>
                    </div>
                  }>
                    <div className="grille g4" style={{ marginBottom: 16 }}>
                      <Kpi libelle="Généré ce mois" valeur={montant(rendement.totalGenereMinor)}
                           detail={`${rendement.vehicules.filter((v) => v.joursLoues > 0).length} voiture(s) ont travaillé sur ${rendement.vehicules.length}`} />
                      <Kpi libelle="Déjà encaissé" valeur={montant(rendement.totalEncaisseMinor)}
                           detail="sur ce qui a été généré" />
                      <Kpi libelle="Réservé pour plus tard" valeur={montant(rendement.totalReserveApresMinor)}
                           detail="déjà promis, au-delà de ce mois" />
                      <Kpi libelle="Voitures immobiles" valeur={String(rendement.vehicules.filter((v) => v.joursLoues === 0 && v.joursReserves === 0).length)}
                           detail="aucun jour loué ni réservé" />
                    </div>

                    <div className="rendement">
                      {rendement.vehicules.map((v) => (
                        <div key={v.vehicleId} className="carte">
                          <div className="rv-tete">
                            <span className="rv-nom">{v.libelle}</span>
                            <span className="rv-montant montant"
                                  style={{ color: v.genereMinor === 0 ? 'var(--danger)' : undefined }}>
                              {montant(v.genereMinor)}
                            </span>
                          </div>
                          <div className="mini sourdine">
                            {v.immatriculation} · {montant(v.tarifJourMinor)} / jour
                          </div>

                          <div className="mini-cal" aria-hidden="true">
                            {v.jours.map((e, i) => (
                              <i key={i} className={`${e !== 'libre' ? e : ''}${estAujourdhui(mois, i) ? ' auj' : ''}`}
                                 title={`${i + 1} — ${LIBELLE_JOUR[e]}`} />
                            ))}
                          </div>

                          <div className="rv-pied">
                            <span>
                              {v.joursLoues} j loué{v.joursLoues > 1 ? 's' : ''}
                              {v.joursReserves > 0 && ` · ${v.joursReserves} réservé(s)`}
                              {v.joursEntretien > 0 && ` · ${v.joursEntretien} au garage`}
                            </span>
                            <span>{v.nbLocations} location(s)</span>
                          </div>

                          {v.reserveApresJours > 0 && (
                            <div className="mini" style={{ marginTop: 8, color: 'var(--violet)' }}>
                              Déjà réservée {v.reserveApresJours} jour(s) après ce mois —{' '}
                              <b>{montant(v.reserveApresMinor)}</b>
                            </div>
                          )}
                          {v.joursLoues === 0 && v.joursReserves === 0 && (
                            <div className="mini" style={{ marginTop: 8, color: 'var(--danger)' }}>
                              Pas sortie de tout le mois.
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </Carte>
                </div>
              )}

              {/* D'ou vient l'argent qui n'est pas de la location. */}
              {entrees && (
                <div className="grille g2" style={{ marginTop: 20, alignItems: 'start' }}>
                  <Carte titre={`Suppléments facturés — ${montant(entrees.supplements.totalMinor)}`} pad={false}>
                    {entrees.supplements.total === 0 ? (
                      <div className="carte-p"><Vide titre="Aucun supplément ce mois-ci">
                        Retards, carburant manquant, kilomètres au-delà du forfait et lavages
                        apparaîtront ici, avec le calcul qui les justifie.
                      </Vide></div>
                    ) : (
                      <div className="enveloppe-tbl">
                        <table className="tbl compact">
                          <thead><tr><th>Supplément</th><th>Calcul</th><th>Contrat</th><th className="droite">Montant</th></tr></thead>
                          <tbody>
                            {entrees.supplements.lignes.map((l, i) => (
                              <tr key={i}>
                                <td><b>{l.quoi}</b><div className="mini sourdine">{l.client}</div></td>
                                <td className="mini sourdine">{l.detail}</td>
                                <td className="mini">{l.contrat}</td>
                                <td className="droite montant">{montant(l.montantMinor)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Carte>

                  <Carte titre={`Cautions — ${montant(entrees.cautions.totalMinor)} détenus`} pad={false}>
                    {entrees.cautions.total === 0 ? (
                      <div className="carte-p"><Vide titre="Aucun mouvement de caution" /></div>
                    ) : (
                      <div className="enveloppe-tbl">
                        <table className="tbl compact">
                          <thead><tr><th>Mouvement</th><th>Détail</th><th>Par</th><th className="droite">Montant</th></tr></thead>
                          <tbody>
                            {entrees.cautions.lignes.map((l, i) => (
                              <tr key={i}>
                                <td><b>{l.quoi}</b><div className="mini sourdine">{dateFr(l.date)}</div></td>
                                <td className="mini sourdine">{l.detail}</td>
                                <td className="mini sourdine">{l.par ?? '—'}</td>
                                <td className="droite montant"
                                    style={{ color: l.montantMinor < 0 ? 'var(--danger)' : undefined }}>
                                  {montant(l.montantMinor)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <p className="mini sourdine" style={{ padding: '10px 14px 12px' }}>
                      Une caution n'est pas un gain : c'est l'argent d'un client, gardé. Elle est
                      retirée de la trésorerie réellement disponible.
                    </p>
                  </Carte>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {vue === 'jour' && (
      <>
      <div className="tiroirs">
        {t.tiroirs.map((x) => (
          <div key={x.devise} className={`tiroir ${x.devise === t.deviseReference ? 'ref' : ''}`}>
            <div className="code">{x.devise} · {x.libelle}</div>
            <div className="solde">{montant(x.soldeMinor, '', x.decimales)}</div>
            {x.devise !== t.deviseReference && <div className="cv">≈ {montant(x.contrevaleurMinor, t.deviseReference)}</div>}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 14 }} className="grille g2">
        <Carte>
          <div className="micro">Total consolidé</div>
          <div className="kpi"><div className="v">{montant(t.totalReferenceMinor, t.deviseReference)}</div></div>
          <div style={{ marginTop: 10 }}>
            <Bandeau type="accent">
              dont <b>{montant(t.tresorerie.prestationsFuturesEncaisseesMinor)}</b> de prestations futures déjà encaissées
              et <b>{montant(t.tresorerie.cautionsDetenuesMinor)}</b> de cautions — <b>non disponibles</b>.
            </Bandeau>
          </div>
        </Carte>
        <Carte className="signature-kpi">
          <div className="micro">Trésorerie réellement disponible</div>
          <div className="kpi"><div className="v" style={{ fontSize: 30 }}>{montant(t.tresorerie.disponibleMinor)}</div></div>
          <div className="decomposition">
            <span>caisse <b>{montant(t.tresorerie.soldeCaisseMinor)}</b></span>
            <span>− futures <b>{montant(t.tresorerie.prestationsFuturesEncaisseesMinor)}</b></span>
            <span>− cautions <b>{montant(t.tresorerie.cautionsDetenuesMinor)}</b></span>
          </div>
        </Carte>
      </div>

      <div style={{ marginTop: 16 }}>
        <Carte titre="Mouvements" pad={false}
               action={<input type="date" value={jour} onChange={(e) => setJour(e.target.value)} style={{ height: 32, borderRadius: 8, border: '1px solid var(--bord-fort)', padding: '0 8px' }} />}>
          {mvts.length === 0 ? <div className="carte-p"><Vide titre="Aucun mouvement ce jour-là" /></div> : (
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <thead>
                  <tr><th>Heure</th><th>Type</th><th>Libellé</th><th>Mode</th><th>Devise</th>
                      <th className="droite">Montant</th><th className="droite">Taux</th>
                      <th className="droite">Contrevaleur</th><th>Agent</th></tr>
                </thead>
                <tbody>
                  {mvts.map((m) => (
                    <tr key={m._id}>
                      <td className="num">{heureFr(m.date)}</td>
                      <td><Badge statut={m.type.startsWith('caution') ? 'retour' : m.type.startsWith('change') ? 'entretien' : ['depense', 'retrait'].includes(m.type) ? 'retard' : 'en_cours'}
                                 libelle={m.type.replace(/_/g, ' ')} /></td>
                      <td>{m.libelle}
                        {m.gainChangeMinor !== undefined && m.gainChangeMinor !== 0 && (
                          <div className="mini" style={{ color: m.gainChangeMinor > 0 ? 'var(--succes)' : 'var(--danger)' }}>
                            {m.gainChangeMinor > 0 ? 'gain' : 'perte'} de change {montant(Math.abs(m.gainChangeMinor))}
                          </div>)}
                      </td>
                      <td className="mini sourdine">{m.mode}</td>
                      <td><b>{m.devise}</b></td>
                      <td className="droite montant">{montant(m.montantMinor, '')}</td>
                      <td className="droite num mini sourdine">{m.devise === t.deviseReference ? '—' : (m.tauxAppliqueMinor / 100).toFixed(2)}</td>
                      <td className="droite montant">{montant(m.contrevaleurMinor, '')}</td>
                      <td className="mini sourdine">{m.creePar?.nom ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
      </div>
      </>
      )}

      {estAgent && (
        <p className="mini sourdine" style={{ marginTop: 12 }}>
          La cloture de caisse est réservée au responsable. Vos encaissements sont definitifs des l'enregistrement.
        </p>
      )}

      {ecran === 'encaisser' && <Encaisser devises={dev} contrat={cible}
        onFini={(m) => { fermerEncaissement(); setMsg(m); recharger(); }} onFermer={fermerEncaissement} />}
      {ecran === 'change' && <Change devises={dev} onFini={(m) => { setEcran(''); setMsg(m); recharger(); }} onFermer={() => setEcran('')} />}
      {ecran === 'cloture' && <Cloture tiroirs={t} onFini={(m) => { setEcran(''); setMsg(m); recharger(); }} onFermer={() => setEcran('')} />}
      {ecran === 'taux' && <Taux devises={dev} onFini={(m) => { setEcran(''); setMsg(m); recharger(); }} onFermer={() => setEcran('')} />}
    </>
  );
}

/** Paiement mixte : un meme reglement en plusieurs devises. */
function Encaisser({ devises, contrat, onFini, onFermer }:
  { devises: Devises; contrat: Contrat | null; onFini: (m: string) => void; onFermer: () => void }) {
  const [libelle, setLibelle] = useState(contrat
    ? `Solde location ${contrat.numero} — ${nomClient(contrat.customer as never)}`
    : 'Encaissement location');
  const [lignes, setLignes] = useState([{
    devise: devises.deviseReference,
    montant: contrat && contrat.soldeMinor > 0 ? (contrat.soldeMinor / 100).toFixed(2) : '',
  }]);
  const [erreur, setErreur] = useState('');

  const tauxDe = (d: string) => d === devises.deviseReference ? 100 : (devises.taux.find((t) => t.devise === d)?.tauxAppliqueMinor ?? 0);
  const total = lignes.reduce((a, l) => a + Math.round((centimes(Number(l.montant || 0)) * tauxDe(l.devise)) / 100), 0);
  /*
   * Sans taux du jour, l'encaissement est refuse par le serveur — et le bouton
   * restait simplement gris, sans un mot. L'agent voyait un bouton mort et
   * n'avait aucun moyen de deviner qu'il manque un taux, ni ou le saisir.
   */
  const sansTaux = lignes
    .filter((l) => Number(l.montant) > 0 && l.devise !== devises.deviseReference && tauxDe(l.devise) === 0)
    .map((l) => l.devise);

  async function envoyer() {
    setErreur('');
    try {
      await api.post('/caisse/paiement-mixte', {
        libelle,
        // Sans `contract`, le serveur encaisse mais ne solde rien.
        contract: contrat?._id,
        reservation: contrat ? (typeof contrat.reservation === 'string' ? contrat.reservation : contrat.reservation._id) : undefined,
        customer: contrat ? (typeof contrat.customer === 'string' ? contrat.customer : contrat.customer._id) : undefined,
        lignes: lignes.filter((l) => Number(l.montant) > 0)
          .map((l) => ({ devise: l.devise, montantMinor: centimes(Number(l.montant)) })),
      });
      onFini(contrat ? `Encaissement rattache au contrat ${contrat.numero}.` : 'Encaissement enregistré.');
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Erreur'); }
  }

  return (
    <Modale titre={contrat ? `Encaisser — contrat ${contrat.numero}` : 'Encaisser'} onFermer={onFermer}>
      {contrat
        ? <div style={{ marginBottom: 12 }}><Bandeau type="info" titre={`Reste dû : ${montant(contrat.soldeMinor)}`}>
            Cet encaissement sera rattache au dossier : le solde du contrat et la tresorerie disponible se mettent a jour.
          </Bandeau></div>
        : <div style={{ marginBottom: 12 }}><Bandeau type="warn" titre="Encaissement non rattache">
            Aucun dossier n'est selectionne : ce montant entrera en caisse sans solder de contrat.
            Pour solder une location, partez du dossier ou de la liste « Soldes a encaisser ».
          </Bandeau></div>}
      <Champ label="Libellé"><input value={libelle} onChange={(e) => setLibelle(e.target.value)} /></Champ>
      {sansTaux.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Bandeau type="ko" titre={`Aucun taux du jour pour ${[...new Set(sansTaux)].join(', ')}`}>
            Sans taux, cet encaissement ne peut pas être converti en {devises.deviseReference} et
            sera refusé. Un gérant le saisit dans <b>Caisse › Taux du jour</b>.
          </Bandeau>
        </div>
      )}
      <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
        {lignes.map((l, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 110px auto', gap: 8, alignItems: 'end' }}>
            <Champ label={i === 0 ? 'Montant' : ''}>
              <input type="number" inputMode="decimal" value={l.montant}
                     onChange={(e) => setLignes(lignes.map((x, j) => j === i ? { ...x, montant: e.target.value } : x))} />
            </Champ>
            <Champ label={i === 0 ? 'Devise' : ''}>
              <select value={l.devise} onChange={(e) => setLignes(lignes.map((x, j) => j === i ? { ...x, devise: e.target.value } : x))}>
                {devises.devises.map((d) => <option key={d.code} value={d.code}>{d.code}</option>)}
              </select>
            </Champ>
            {lignes.length > 1 && <button className="btn btn-s btn-f" onClick={() => setLignes(lignes.filter((_, j) => j !== i))}>retirer</button>}
          </div>
        ))}
        {lignes.map((l, i) => l.devise !== devises.deviseReference && Number(l.montant) > 0 ? (
          <div key={`c${i}`} className="mini sourdine">
            {Number(l.montant).toFixed(2)} {l.devise} ≈ {montant(Math.round((centimes(Number(l.montant)) * tauxDe(l.devise)) / 100))} (taux {(tauxDe(l.devise) / 100).toFixed(2)})
          </div>
        ) : null)}
        <button className="btn btn-s" onClick={() => setLignes([...lignes, { devise: 'EUR', montant: '' }])}>
          + Ajouter une devise (paiement mixte)
        </button>
      </div>
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid var(--bord)' }}>
        <b>Total en {devises.deviseReference}</b><b className="montant" style={{ fontSize: 20 }}>{montant(total)}</b>
      </div>
      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>Annuler</button>
        <button className="btn btn-p" disabled={total <= 0 || sansTaux.length > 0} onClick={() => void envoyer()}>Encaisser</button>
      </div>
    </Modale>
  );
}

function Change({ devises, onFini, onFermer }: { devises: Devises; onFini: (m: string) => void; onFermer: () => void }) {
  const [ds, setDs] = useState('EUR'); const [ms, setMs] = useState('');
  const [de, setDe] = useState(devises.deviseReference); const [me, setMe] = useState('');
  const [erreur, setErreur] = useState('');
  const taux = (d: string) => d === devises.deviseReference ? 100 : (devises.taux.find((t) => t.devise === d)?.tauxAppliqueMinor ?? 0);
  const vSortie = Math.round((centimes(Number(ms || 0)) * taux(ds)) / 100);
  const vEntree = Math.round((centimes(Number(me || 0)) * taux(de)) / 100);

  async function envoyer() {
    setErreur('');
    try {
      await api.post('/caisse/change', {
        deviseSortie: ds, montantSortieMinor: centimes(Number(ms)),
        deviseEntree: de, montantEntreeMinor: centimes(Number(me)),
      });
      onFini('Opération de change enregistrée.');
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Erreur'); }
  }

  return (
    <Modale titre="Opération de change" onFermer={onFermer}>
      <div className="grille g2">
        <div>
          <div className="micro">Sortie de caisse</div>
          <Champ label="Montant"><input type="number" value={ms} onChange={(e) => setMs(e.target.value)} /></Champ>
          <Champ label="Devise"><select value={ds} onChange={(e) => setDs(e.target.value)}>{devises.devises.map((d) => <option key={d.code}>{d.code}</option>)}</select></Champ>
        </div>
        <div>
          <div className="micro">Entrée en caisse</div>
          <Champ label="Montant"><input type="number" value={me} onChange={(e) => setMe(e.target.value)} /></Champ>
          <Champ label="Devise"><select value={de} onChange={(e) => setDe(e.target.value)}>{devises.devises.map((d) => <option key={d.code}>{d.code}</option>)}</select></Champ>
        </div>
      </div>
      {vSortie > 0 && vEntree > 0 && (
        <div style={{ marginTop: 12 }}>
          <Bandeau type={vEntree - vSortie >= 0 ? 'ok' : 'warn'}
                   titre={`${vEntree - vSortie >= 0 ? 'Gain' : 'Perte'} de change : ${montant(Math.abs(vEntree - vSortie))}`}>
            Sortie {montant(vSortie)} · entree {montant(vEntree)} (contrevaleurs au taux du jour)
          </Bandeau>
        </div>
      )}
      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>Annuler</button>
        <button className="btn btn-p" disabled={!vSortie || !vEntree} onClick={() => void envoyer()}>Enregistrer le change</button>
      </div>
    </Modale>
  );
}

function Cloture({ tiroirs, onFini, onFermer }: { tiroirs: Tiroirs; onFini: (m: string) => void; onFermer: () => void }) {
  const [c, setC] = useState<Record<string, { compte: string; motif: string }>>(
    Object.fromEntries(tiroirs.tiroirs.map((t) => [t.devise, { compte: String(t.soldeMinor / 100), motif: '' }])));
  const [erreur, setErreur] = useState('');

  async function envoyer() {
    setErreur('');
    try {
      await api.post('/caisse/cloture', {
        comptages: tiroirs.tiroirs.map((t) => ({
          devise: t.devise, compteMinor: centimes(Number(c[t.devise]?.compte ?? 0)), motif: c[t.devise]?.motif,
        })),
      });
      onFini('Caisse clôturée.');
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Erreur'); }
  }

  return (
    <Modale titre="Clôture de caisse — comptage par devise" onFermer={onFermer}>
      <div style={{ display: 'grid', gap: 12 }}>
        {tiroirs.tiroirs.map((t) => {
          const compte = centimes(Number(c[t.devise]?.compte ?? 0));
          const ecart = compte - t.soldeMinor;
          return (
            <div key={t.devise} style={{ borderBottom: '1px solid var(--bord)', paddingBottom: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr 1fr', gap: 10, alignItems: 'end' }}>
                <b>{t.devise}</b>
                <Champ label="Théorique"><input readOnly value={montant(t.soldeMinor, '', t.decimales)} /></Champ>
                <Champ label="Compte">
                  <input type="number" value={c[t.devise]?.compte ?? ''}
                         onChange={(e) => setC({ ...c, [t.devise]: { compte: e.target.value, motif: c[t.devise]?.motif ?? '' } })} />
                </Champ>
              </div>
              {ecart !== 0 && (
                <div style={{ marginTop: 8 }}>
                  <Champ label={`Ecart de ${montant(ecart, t.devise, t.decimales)} — motif obligatoire`}>
                    <input value={c[t.devise]?.motif ?? ''}
                           onChange={(e) => setC({ ...c, [t.devise]: { compte: c[t.devise]?.compte ?? '', motif: e.target.value } })} />
                  </Champ>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>Annuler</button>
        <button className="btn btn-p" onClick={() => void envoyer()}>Clôturer</button>
      </div>
    </Modale>
  );
}

function Taux({ devises, onFini, onFermer }: { devises: Devises; onFini: (m: string) => void; onFermer: () => void }) {
  const etrangeres = devises.devises.filter((d) => d.code !== devises.deviseReference);
  const [v, setV] = useState<Record<string, string>>(Object.fromEntries(
    etrangeres.map((d) => [d.code, String((devises.taux.find((t) => t.devise === d.code)?.tauxAppliqueMinor ?? 0) / 100)])));
  const [erreur, setErreur] = useState('');

  async function envoyer() {
    setErreur('');
    try {
      for (const d of etrangeres) {
        const val = centimes(Number(v[d.code] ?? 0));
        if (val > 0) await api.post('/caisse/taux', { devise: d.code, tauxReferenceMinor: val, tauxAppliqueMinor: val });
      }
      onFini('Taux mis à jour — les mouvements passes ne sont pas modifies.');
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Erreur'); }
  }

  return (
    <Modale titre="Taux appliqués en agence" onFermer={onFermer}>
      <p className="mini sourdine" style={{ marginBottom: 12 }}>
        Le taux appliqué en agence n'est pas celui de la banque. Chaque taux est historise avec sa date d'effet,
        et <b>les mouvements déjà enregistrés gardent leur contrevaleur d'origine</b>.
      </p>
      <div style={{ display: 'grid', gap: 10 }}>
        {etrangeres.map((d) => (
          <Champ key={d.code} label={`1 ${d.code} = ? ${devises.deviseReference}`}>
            <input type="number" step="0.01" value={v[d.code] ?? ''} onChange={(e) => setV({ ...v, [d.code]: e.target.value })} />
          </Champ>
        ))}
      </div>
      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>Annuler</button>
        <button className="btn btn-p" onClick={() => void envoyer()}>Enregistrer les taux</button>
      </div>
    </Modale>
  );
}
